import { Col, message, Modal, Row, Spin, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEffectOnce } from "react-use";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import { SearchOutlined } from "@ant-design/icons";
import { GreyTableContainer } from "../../../components/Table/GreyTable";
import { FlexCol, FlexRow } from "../../../components/Container/Container";
import dayjs from "dayjs";
import color from "../../../resource/color";
import icon from "../../../resource/icon";
import { numberFormatter } from "../../../utility/Formatter";
import Input from "../../../components/Input/Input";
import { RangePicker } from "../../../components/DatePicker/DatePicker";
import Select from "../../../components/Select/Select";
import {
  InvoiceOrderStatus,
  invoiceStatusLabel,
  CreditType,
  creditTypeLabel,
  invoiceStatusColor,
  invoiceOrderStatusLabel,
  invoiceOrderStatusColor,
} from "../../../definitions/invoice";
import {
  getDetail,
  getOrderCreditPayment,
  syncInvoiceCancelPayment,
  syncInvoicePayment,
} from "../../../datasource/OrderCreditDatasource";
import { useRecoilState } from "recoil";
import { creditLimitDetailState } from "../../../store/CreditLimitDetail";
import axios, { AxiosRequestConfig } from "axios";
import { NAV_URL } from "../../../config/develop-config";

const datePattern = "DD/MM/YYYY";
const REQUEST_DMY = "YYYY-MM-DD";
export const CreditUsingHistory: React.FC = () => {
  const userProfile = JSON.parse(localStorage.getItem("profile")!);
  const { company, firstname, lastname } = userProfile;
  const navigate = useNavigate();

  const { pathname } = window.location;
  const pathSplit = pathname.split("/") as Array<string>;
  const id = pathSplit[3];

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>();

  const [syncing, setSyncing] = useState(false);

  const [search, setSearch] = useState<string>();
  const [dateFilter, setDates] = useState<any>();
  const [creditType, setCreditType] = useState<CreditType>(CreditType.ALL);
  const [status, setStatus] = useState<InvoiceOrderStatus[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const [recoilState, setRecoilState] = useRecoilState(creditLimitDetailState);
  const [customerNo, setCustomerNo] = useState<string>();

  const [syncCC, setSyncCC] = useState<any[]>();

  useEffectOnce(() => {
    fetchData();
  });

  useEffect(() => {
    setPage(1);
    fetchData(1);
  }, [search, dateFilter, creditType, status]);

  const fetchData = async (pageNo?: number) => {
    setLoading(true);
    const res = await getOrderCreditPayment({
      company,
      customerCompanyId: id,
      search,
      startDate: dateFilter && dateFilter[0] ? dateFilter[0].format(REQUEST_DMY) : undefined,
      endDate: dateFilter && dateFilter[1] ? dateFilter[1].format(REQUEST_DMY) : undefined,
      creditType: creditType === CreditType.ALL ? undefined : creditType,
      status: status,
      page: pageNo || page,
      take: pageSize,
    });
    setData(res?.data);
    setLoading(false);
  };

  const syncData = async () => {
    setSyncing(true);
    let customerNo = recoilState.customerNo;
    if (!customerNo) {
      console.log("reload");
      const detail = await getDetail(id);
      customerNo = detail.customerNo;
      setRecoilState({ ...recoilState, customerNo });
    }
    setCustomerNo(customerNo);
    await syncInvoicePayment({
      company,
      customerNo,
    })
      .then((res) => {
        if (res?.success) {
          message.success("อัปเดตข้อมูลแล้ว");
          setPage(1);
          setSearch("");
          setDates([null, null]);
          setCreditType(CreditType.ALL);
          setStatus([]);
        } else {
          message.error("เกิดข้อผิดพลาด");
        }
      })
      .catch((err) => {
        console.log(err);
        message.error("เกิดข้อผิดพลาด");
      })
      .finally(() => {
        setSyncing(false);
      });
  };

  const onSyncCancel = async (invoiceNo: string) => {
    setSyncCC([...(syncCC || []), invoiceNo]);
    await syncInvoiceCancelPayment({ company, invoiceNo }).then((res) => {
      setSyncing(false);
      message.success("อัปเดตข้อมูลแล้ว");
      fetchData();
    });
  };

  const columns = [
    {
      title: "วันที่ออกเอกสาร",
      dataIndex: "invDate",
      key: "invDate",
      render: (date: string, row: any) => {
        return (
          <FlexCol>
            <Text>{date ? dayjs(date).format(datePattern) : "-"}</Text>
            <Text color='Text3' level={6}>
              &nbsp;
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "Invoice No.",
      dataIndex: "invoiceNo",
      render: (invoiceNo: string, row: any) => {
        return (
          <FlexCol>
            <Text>{invoiceNo}</Text>
            <Text color='Text3' level={6}>
              &nbsp;
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "ประเภท",
      dataIndex: "creditType",
      key: "creditType",
      render: (type: string) => (
        <FlexCol>
          <Text>{creditTypeLabel[type]}</Text>
          <Text color='Text3' level={6}>
            &nbsp;
          </Text>
        </FlexCol>
      ),
    },
    {
      title: "ยอดรวมทั้งหมด",
      dataIndex: "price",
      key: "price",
      render: (amt: number, row: any) => {
        return (
          <FlexCol>
            <Text>{numberFormatter(amt, 2)}</Text>
            <Text color='Text3' level={6}>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "ยอดชำระเงิน",
      dataIndex: "payment",
      key: "payment",
      render: (amt: number, row: any) => {
        return (
          <FlexCol>
            <Text>{numberFormatter(amt, 2)}</Text>
            <Text color='Text3' level={6}>
              บาท
            </Text>
          </FlexCol>
        );
      },
    },
    {
      title: "สถานะ",
      dataIndex: "status",
      render: (status) => {
        return (
          <Tag color={invoiceOrderStatusColor[status]} style={{ borderRadius: 12 }}>
            <Text color='white' fontWeight={600} fontSize={14}>
              {invoiceOrderStatusLabel[status]}
            </Text>
          </Tag>
        );
      },
    },
    {
      title: "",
      dataIndex: "invoiceNo",
      key: "sync",
      width: "5%",
      render: (invoiceNo: string, row: any) => {
        return (
          <div
            className='btn btn-icon btn-light btn-hover-primary btn-sm'
            onClick={() => onSyncCancel(invoiceNo)}
            style={{ cursor: "pointer" }}
          >
            {syncCC?.includes(invoiceNo) ? (
              <Spin style={{ fontSize: 10 }} />
            ) : (
              <span className='svg-icon svg-icon-primary svg-icon-2x'>
                {<img src={icon.syncIcon2} style={{ width: 15, height: 16 }} />}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <Row>
        <Col span={20}>
          <Text fontWeight={700}>ประวัติการใช้งานวงเงิน</Text>
          <br />
          <Text level={6}>
            อัปเดตล่าสุดเมื่อ{" "}
            {data?.latestUpdate ? dayjs(data?.latestUpdate).format("DD/MM/YYYY, HH:mm") : "-"}
          </Text>
        </Col>
        <Col span={4}>
          <Button
            title='อัปเดตข้อมูลใหม่'
            typeButton='border-primary'
            icon={<img src={icon.reload} style={{ width: 15, height: 17, marginBottom: 4 }} />}
            onClick={() => syncData()}
            loading={syncing}
          />
        </Col>
      </Row>
      <Row justify='end' gutter={12} style={{ marginTop: 12 }}>
        <Col span={8}>
          <Input
            placeholder='รหัส Invoice No.'
            suffix={<SearchOutlined />}
            onPressEnter={(e) => {
              const value = (e.target as HTMLTextAreaElement).value;
              setSearch(value);
            }}
          />
        </Col>
        <Col span={4} />
        <Col span={4}>
          <RangePicker
            enablePast
            allowEmpty={[true, true]}
            onChange={(dateFilter, dateString) => {
              setDates(dateFilter);
            }}
            value={dateFilter}
          />
        </Col>
        <Col span={4}>
          <Select
            data={Object.entries(creditTypeLabel).map(([key, label]) => ({
              key,
              label,
              value: key,
            }))}
            placeholder={creditTypeLabel[CreditType.ALL]}
            defaultValue={CreditType.ALL}
            onChange={(val) => setCreditType(val)}
            style={{ width: "100%" }}
            allowClear={false}
            value={creditType}
          />
        </Col>
        <Col span={4}>
          <Select
            data={Object.entries(invoiceOrderStatusLabel)
              .filter(([key]) => key !== InvoiceOrderStatus.ALL)
              .map(([key, label]) => ({
                key,
                label,
                value: key,
              }))}
            placeholder={invoiceOrderStatusLabel[InvoiceOrderStatus.ALL]}
            // defaultValue={[]}
            onChange={(val) => setStatus(val)}
            style={{ width: "100%" }}
            allowClear={false}
            value={status}
            mode='multiple'
          />
        </Col>
      </Row>
      <br />
      <Row justify='end'>
        <Col span={4}>
          <FlexRow align='center' justify='end' style={{ width: "100%", height: "100%" }}>
            <Text>ทั้งหมด {data?.count || 0} รายการ</Text>
          </FlexRow>
        </Col>
      </Row>
      <br />
      <GreyTableContainer>
        <Table
          className='rounded-lg'
          columns={columns}
          dataSource={data?.data || []}
          pagination={{
            pageSize,
            total: data?.count || 0,
            showSizeChanger: false,
            onChange: (page) => {
              setPage(page);
              fetchData(page);
            },
            position: ["bottomCenter"],
          }}
          size='large'
          tableLayout='fixed'
          // loading={loading}
        />
      </GreyTableContainer>
    </>
  );
};
